import { animate, state, style, transition, trigger } from "@angular/animations";
import { isPlatformBrowser, NgIf } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, Input, NgZone, OnInit, PLATFORM_ID, Renderer2, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { IconAppRegistrationComponent } from "@src/app/components/svg/icon-app-registration/icon-app-registration.component";
import { IconEmailComponent } from "@src/app/components/svg/icon-email/icon-email.component";
import { IconCloseComponent } from "@src/app/icon-close/icon-close.component";
import { CookieService } from "ngx-cookie-service";
import { firstValueFrom } from "rxjs";
import { ConfigService } from "src/app/servicios/config/config.service";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { environment } from "../../../environments/environment";
import { IconPauseComponent } from "../../components/svg/icon-pause/icon-pause.component";
import { IconPlayComponent } from "../../components/svg/icon-play/icon-play.component";
import { KebabCasePipe } from "../../pipes/kebab-case.pipe";
import { IframeService } from "../../servicios/iframe/iframe.service";
import { ReelsService } from "../../servicios/reels.service";
import { SidenavService } from "../../servicios/sidenav/sidenav.service";
import { IconMenuBurguerComponent } from "../icon-menu-burguer/icon-menu-burguer.component";
import { IconSearchComponent } from "../icon-search/icon-search.component";
import { ModalLoginComponent } from "../modal-login/modal-login.component";
import { SidenavMenuBurguerComponent } from "../sidenav-menu-burguer/sidenav-menu-burguer.component";
import { SkeletonImageComponent } from "../skeleton-image/skeleton-image/skeleton-image.component";

@Component({
  selector: "app-common-menu-burguer",
  templateUrl: "./common-menu-burguer.component.html",
  styleUrls: ["./common-menu-burguer.component.scss"],
  imports: [FormsModule, RouterLink, SidenavMenuBurguerComponent, IconMenuBurguerComponent, IconSearchComponent, NgIf, IconCloseComponent, IconEmailComponent, IconAppRegistrationComponent, ModalLoginComponent, SkeletonImageComponent, IconPlayComponent, IconPauseComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [trigger("slideInOut", [transition(":enter", [style({ transform: "translateY(-100%)" }), animate("200ms ease-in", style({ transform: "translateY(0%)" }))]), transition(":leave", [animate("200ms ease-in", style({ transform: "translateY(-100%)" }))])]), trigger("fade", [state("inactive", style({ opacity: 0 })), state("active", style({ opacity: 1 })), transition("* <=> *", [animate(2000)])])],
})
export class CommonMenuBurguerComponent implements OnInit {
  // Variables principales del componente
  @Input() resizable: boolean; // Permite que el menú se redimensione
  @Input() configPrensa: any; // Configuración de la aplicación
  boolAudio: boolean = false; // Estado del audio (0: pausado, 1: reproduciendo)
  configBloque: any; // Configuración específica del bloque del menú
  constantesGlobales; // Constantes globales de la aplicación
  contactoExterno: any = false; // Indica si el contacto es externo
  enlaceRegistroExterno: string; // URL para registro externo
  isInIframe: boolean = false; // Se actualiza en el cliente
  isRegistroExterno: boolean = false; // Indica si se utiliza registro externo
  mostrarBuscador = false; // Muestra u oculta el buscador
  parteCentralContent; // Contenido HTML de la parte central del menú
  show24h = false; // Indica si se muestra la opción de noticias 24h
  showBuscador = false; // Indica si se muestra el buscador
  showContacto: any = false; // Indica si se muestra la información de contacto
  showLogin = false; // Indica si se muestra la opción de login
  showParteCentral = false; // Indica si se muestra la parte central del menú
  showRadio = false; // Indica si se muestra la opción de radio
  urlContacto: any; // URL de contacto
  urlLogoMovil: string; // URL del logo para dispositivos móviles
  userLogin = null; // Objeto con los datos del usuario logueado
  showSidenavMenu = false; // Inicialmente oculto
  @ViewChild("burgerButton") burgerButton: ElementRef;
  @ViewChild("audioRadio") audioRadio: ElementRef;
  showLogOutOption: boolean = false;
  showLoginModal: boolean = false;
  globalConstants: any;

  constructor(
    private readonly sidenav: SidenavService,
    private readonly configService: ConfigService,
    private readonly iframeService: IframeService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly kebabPipe: KebabCasePipe,
    private readonly platformService: PlatformService,
    private readonly cookieService: CookieService,
    private readonly elementRef: ElementRef,
    private readonly domSanitizer: DomSanitizer,
    private readonly renderer2: Renderer2,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
    private readonly reelsService: ReelsService,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
  ) {
    if (this.platformService.isBrowser()) {
      const urlParams = new URLSearchParams(window.location.search);
      const isInIframeParam = urlParams.get("inIframe") === "true";
      
      // ✅ DETECCIÓN SÍNCRONA Y ROBUSTA
      this.isInIframe = isInIframeParam || (window.self !== window.top);
    }
  }

  /**
   * ngOnInit:
   * - Inicializa estados de usuario y dispositivo.
   * - Carga la configuración del bloque y ajusta las propiedades CSS.
   * - Configura eventos de scroll según la configuración.
   */
  ngOnInit(): void {
    if (this.platformService.isBrowser()) {
      this.setupMenuVisibility();
      const cookieValue = this.cookieService.get("userLogin" + environment.name);
      if (cookieValue) this.userLogin = JSON.parse(cookieValue);
    }

    // Función para asegurar que la URL tenga https
    const ensureHttps = (url) => {
      if (!url) return url;
      if (url.startsWith("http://") || url.startsWith("https://")) {
        return url.replace("http://", "https://");
      }
      if (url.startsWith("//")) {
        return "https:" + url;
      }
      return "https://" + url;
    };

    const selectorName = this.elementRef.nativeElement.tagName.toLowerCase();
    if (this.configPrensa) {
      this.configBloque = this.configPrensa.general[selectorName];

      if (!this.configBloque.bloque_activo) return;
      this.globalConstants = this.configPrensa.general["global-constants"];
      this.showRadio = this.configBloque.mostrar_radio === "true";
      this.show24h = this.configBloque.mostrar_noticias24h === "true";
      this.showBuscador = this.configBloque.mostrar_buscador === "true";
      this.showLogin = this.configBloque.mostrar_login === "true";
      this.isRegistroExterno = this.configBloque.is_registro_externo === "true";
      this.enlaceRegistroExterno = this.configBloque.enlace_registro_externo;
      this.showParteCentral = this.configBloque.mostrar_parte_central === "true";
      if (this.showParteCentral) {
        this.parteCentralContent = this.domSanitizer.bypassSecurityTrustHtml(this.configBloque.info_central);
      }
      
      this.showContacto = this.configBloque.mostrar_contacto === "true";
      this.urlLogoMovil = ensureHttps(this.configBloque.logo_menu_movil);
      if (this.showContacto) {
        this.contactoExterno = this.configBloque.contacto_externo === "true";
        this.urlContacto = this.configBloque.url_contacto;
      }
    }

    // Detectar si está en iframe por parámetro URL o servicio
    if (this.platformService.isBrowser() && !this.isInIframe) {
      if (window.self !== window.top) {
        this.isInIframe = true;
        this.cdr.detectChanges();
      }
    }

    const inIframe = this.iframeService.getInIframe();
    if (inIframe) {
      firstValueFrom(inIframe).then((result) => {
        if (result !== null) {
          this.isInIframe = result;
          this.cdr.detectChanges();
        }
      });
    }
    this.constantesGlobales = this.configService.getConstantesGlobales();
  }

  /**
   * openSidenav:
   * - Abre el menú lateral (sidenav).
   */
  openSidenav(): void {
    this.showSidenavMenu = true;
    this.sidenav.openSidenav();
  }

  /**
   * onLogoClick:
   * - El logo del header solo navegaba a "/" sin desactivar el modo reels (isReels), así que si
   *   estabas viendo una noticia en modo reels y volvías al home por el logo, la siguiente
   *   noticia que abrías seguía en modo reels — había que pulsar el icono de reels y luego el de
   *   home para desactivarlo. El logo es una señal clara de "sácame de aquí", así que también
   *   debe apagar el modo reels.
   */
  onLogoClick(): void {
    this.reelsService.setIsReels(false);
  }

  setupMenuVisibility(): void {
    // Simular condiciones de @defer (hover e idle) en el cliente
    this.ngZone.runOutsideAngular(() => {
      const button = this.burgerButton?.nativeElement;
      if (button) {
        this.renderer2.listen(button, "mouseenter", () => {
          this.ngZone.run(() => (this.showSidenavMenu = true));
        });
      }
    });
  }

  /**
   * enviarBusqueda:
   * - Procesa y envía la búsqueda, navegando a la ruta correspondiente.
   * @param data Datos del formulario de búsqueda.
   */
  enviarBusqueda(data): void {
    const termino = data?.buscar.trim();
    if (!termino) return;
    const ruta = ["buscar", this.kebabPipe.transform(termino)];
    this.router.navigate(ruta);
    this.mostrarBuscador = false;
  }

  /**
   * playPause:
   * - Alterna la reproducción del audio de radio y actualiza el botón correspondiente.
   */
  playPause(): void {
    const audio = this.audioRadio.nativeElement;
    audio.pause();
    if (!this.boolAudio) {
      audio.play();
    }
    this.boolAudio = !this.boolAudio;
  }

  /**
   * logout:
   * - Cierra la sesión del usuario y elimina la cookie correspondiente.
   * - Recarga la página si se está en el navegador.
   */
  logout(): void {
    this.cookieService.delete("userLogin" + environment.name);
    if (!this.platformService.isBrowser()) return;
    window.location.reload();
  }
}
